import '@sass/common/theme.scss';
import CwLoadModules from '@customjs/module-loader.js';
import CwThemeFramework from '@customjs/generalFns.js';
import { MatchHeight } from 'js-match-height';

/**
 * Running mixed functions across the theme
 */
CwThemeFramework.generalFns();

/**
 * Bootstrap Tabs
 */
const tabs = document.querySelector('[role="tablist"]');

if (tabs) CwLoadModules.Tabs();

/**
 * Dropdown
 */
const dropdown = document.querySelector('.dropdown-toggle');

if (dropdown) CwLoadModules.Dropdown();

/**
 * Accordions
 */
const accordion = document.querySelector('.accordion');

if (accordion) CwLoadModules.Collapse();

/**
 * Service Worker
 */

// check if service worker is available

if ('serviceWorkerNO' in navigator) {
    document.addEventListener('DOMContentLoaded', () => {
        // Registering Service Worker
        navigator.serviceWorker
            // eslint-disable-next-line no-undef
            .register(`${ThemeURLS.childThemeURL}/sw.php`, {
                scope: '/',
            })
            .then((reg) => console.log(reg.scope))
            .catch((error) => console.log(error.message));
    });
}

/**
 * Loading Mobile Menu
 */

function matchMobile() {
    // The mobile header, including the menu button, is shown below the xl breakpoint.
    const mobileMediaQuery = window.matchMedia('(max-width: 1199.98px)');
    return mobileMediaQuery.matches;
}

matchMobile() ? CwThemeFramework.mobileMenu() : '';

window.addEventListener('resize', () => {
    matchMobile() ? CwThemeFramework.mobileMenu() : '';
});

/**
 * Loading Modals in the Page
 * We will be pulling all the buttons with the data-cw-target
 * Attribute in order to initialize all the modals.
 */

// Checking for all out buttons
const modals = document.querySelectorAll('[data-cw-target]');
if (modals.length) {
    const modalInstances = [];
    CwLoadModules.modals().then((CwModals) => {
        modals.forEach((modalBtn, index) => {
            modalInstances[index] = new CwModals('.cw-modal');
            modalInstances[index].init();
        });
    });
}

/**
 * Loading Swiper JS
 * https://swiperjs.com/
 */
const carouselModules = {
    'results-slider': 'results-carousel',
    'results-slider__sidebar': 'results-slider-sidebar',
    'awards-wrap': 'awards-carousel',
    'awards-wrap-sidebar': 'awards-carousel-sidebar',
    'testimonials-section-slider': 'testimonials-carousel',
    'testimonials-section-slider-sidebar': 'testimonials-carousel-sidebar',
    'written-testimonials-slider': 'written-testimonials-sidebar',
    'pa-testimonials-slider': 'testimonials-carousel-pa',
    'steven-testimonials-slider': 'testimonials-carousel-steven',
    'steven-photos-slider': 'photos-carousel-steven',
    'meet-our-attorneys-slider': 'team-carousel',
    'grid-videos': 'videos-carousel',
    'our-team': 'team-sidebar-widget',
    'victories-list': 'victories-carousel',
    'settlements-map': 'settlements-map',
    'videos-section-slider': 'videos-slider',
};

// eslint-disable-next-line no-restricted-syntax
for (const [key, value] of Object.entries(carouselModules)) {
    const name = document.getElementById(key);
    if (name) {
        CwLoadModules.swiperSliders(value).then((module) => {
            module.default();
        });
    }
}

/**
 * Loading MatchHeight
 * Create new instance of MatchHeight [new MatchHeight()]
 * for elements that need to have the same height
 * https://www.npmjs.com/package/js-match-height
 */

const testimonialItem = new MatchHeight('.testimonials-section__item');
const paTestimonialItem = new MatchHeight('.pa-testimonials__item');
const whychooseusItem = new MatchHeight('.why-choose-us__item');

/**
 * Smooth Scroll
 * add class '.scroll' to new elements
 * class '.scroll' already added to footer back to top and TOC dynamic link elements
 */
// smooth scroll
document.addEventListener(
    'click',
    (event) => {
        const link = event.target.closest('.scroll, .toc-table-list a[href^="#"]');
        if (!link) return;

        const hashValue = link.getAttribute('href');
        if (!hashValue || hashValue.charAt(0) !== '#') return;

        let target;
        try {
            target = document.querySelector(hashValue);
        } catch (error) {
            return;
        }
        if (!target) return;

        const header = document.querySelector('.header--sticky');
        const isTocLink = link.closest('.toc, .toc-table-list, .mobile-toc-widget-container, .sidebar-toc, .sidebar-toc-mobile');
        const offsetForStickyHeader = header && isTocLink;

        if (offsetForStickyHeader) {
            const gap = 24;
            const top = target.getBoundingClientRect().top + window.scrollY - Math.ceil(header.getBoundingClientRect().height) - gap;

            window.scrollTo({
                top: Math.max(0, top),
                behavior: 'smooth',
            });
        } else {
            target.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }

        history.pushState(null, null, hashValue);
        event.preventDefault();
    },
    false
);

/**
 * Load More
 */
const loadMore = null || document.querySelector('.load-more');
const categoryFilter = null || document.querySelector('.cw-filter');
const resultFilter = document.getElementById('dss-archive-result-filter');

if (loadMore || categoryFilter || resultFilter) {
    const enablePagination = loadMore ? loadMore.dataset.pagination : undefined;

    CwLoadModules.AjaxCalls().then((cwAjaxLib) => {
        const ajaxCalls = new cwAjaxLib({
            enablePagination: enablePagination,
        });
        ajaxCalls.init();
        if (categoryFilter) ajaxCalls.filter(categoryFilter);
    });
}

/**
 * Results archive practice-area filter
 */
if (resultFilter) {
    import(
        /* webpackChunkName: 'archive-result-filter' */
        '@modules/archive-result-filter'
    ).then((module) => {
        module.default();
    });
}

/**
 * Glossary letter tabs
 */
const glossary = document.querySelector('.glossary');

if (glossary) {
    import(
        /* webpackChunkName: 'glossary-js' */
        '@modules/glossary'
    );
}

/**
 * Related information grid pagination
 */
const relatedInformationGrid = document.querySelector('.related-information-grid');
const relatedInformationPagination = document.querySelector('.related-information-pagination');

if (relatedInformationGrid && relatedInformationPagination) {
    import(
        /* webpackChunkName: 'related-information-pagination' */
        '@modules/related-information-pagination'
    ).then((module) => {
        module.default();
    });
} 


/**
 * Header Search
 * Toggles the collapsed search form in the desktop and mobile headers.
 */
const headerSearches = Array.from(
	document.querySelectorAll('[data-header-search]')
);

if (headerSearches.length) {
	const isSearchOpen = (instance) => instance.classList.contains('is-open');

	const closeSearch = (instance, restoreFocus = false) => {
		instance.classList.remove('is-open');
		const toggle = instance.querySelector('[data-header-search-toggle]');
		toggle.setAttribute('aria-expanded', 'false');

		if (restoreFocus) {
			toggle.focus();
		}
	};

	const openSearch = (instance) => {
		// Only one form may be expanded at a time.
		headerSearches
			.filter((item) => item !== instance)
			.forEach(closeSearch);

		instance.classList.add('is-open');
		instance
			.querySelector('[data-header-search-toggle]')
			.setAttribute('aria-expanded', 'true');

		// The field is only focusable once the transition makes it visible.
		window.requestAnimationFrame(() =>
			instance.querySelector('[data-header-search-field]').focus()
		);
	};

	headerSearches.forEach((instance) => {
		const toggle = instance.querySelector('[data-header-search-toggle]');
		const field = instance.querySelector('[data-header-search-field]');
		const form = instance.querySelector('form');
		const closeButton = instance.querySelector('[data-header-search-close]');

		if (!toggle || !field || !form) return;

		toggle.addEventListener('click', () => {
			if (isSearchOpen(instance)) {
				closeSearch(instance);
				return;
			}

			openSearch(instance);
		});

		if (closeButton) {
			closeButton.addEventListener('click', () => {
				closeSearch(instance, true);
			});
		}

		// An empty query would send the visitor to a results page with no term.
		form.addEventListener('submit', (event) => {
			if (field.value.trim()) return;

			event.preventDefault();
			field.focus();
		});
	});

	document.addEventListener('keydown', (event) => {
		if (event.key !== 'Escape') return;

		headerSearches.filter(isSearchOpen).forEach((instance) => {
			closeSearch(instance, true);
		});
	});

	document.addEventListener('click', (event) => {
		headerSearches
			.filter(isSearchOpen)
			.filter((instance) => !instance.contains(event.target))
			.forEach(closeSearch);
	});
}

/**
 * Legal Services accordion
 * Below 992px, tapping a practice-area card expands its excerpt.
 * At 992px and up, excerpts stay visible and the card is a link.
 */
const practiceAreaItems = document.querySelectorAll(
	'.c-practice-areas .pa__item.has-excerpt'
);

if (practiceAreaItems.length) {
	const desktopQuery = window.matchMedia('(min-width: 992px)');

	const syncExpandedState = () => {
		practiceAreaItems.forEach((item) => {
			const link = item.querySelector('.main-link');
			if (!link) return;

			if (desktopQuery.matches) {
				link.removeAttribute('aria-expanded');
				return;
			}

			link.setAttribute(
				'aria-expanded',
				item.classList.contains('is-open') ? 'true' : 'false'
			);
		});
	};

	practiceAreaItems.forEach((item) => {
		item.addEventListener('click', (event) => {
			if (desktopQuery.matches) return;
			if (event.target.closest('.pa__item-content-button, .pa__item-arrow')) return;

			const clickedContent = event.target.closest('.pa__item-content');
			if (item.classList.contains('is-open') && clickedContent) return;

			event.preventDefault();
			item.classList.toggle('is-open');
			syncExpandedState();
		});
	});

	syncExpandedState();

	if (typeof desktopQuery.addEventListener === 'function') {
		desktopQuery.addEventListener('change', syncExpandedState);
	} else if (typeof desktopQuery.addListener === 'function') {
		desktopQuery.addListener(syncExpandedState);
	}
}
